import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditPrescriberMutation } from "@modules/prescribers/services/mutations";
import { PrescriberFormValues, prescriberSchema } from "@modules/prescribers/validations";
import { listPrescribersQueryKey } from "@modules/prescribers/services/queries/listPrescribers/query";
import { EditPrescriberModalProps } from "./types";

export function useEditPrescriberModal({ modalRef, onComplete }: EditPrescriberModalProps) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<PrescriberFormValues>({
    resolver: zodResolver(prescriberSchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditPrescriberMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listPrescribersQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("prescriber_admin.success.edit"),
      });

      onComplete();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: PrescriberFormValues) => {
    const prescriberId = modalRef.current?.getId();
    await mutateAsync({ id: prescriberId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
